import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { STRIKES, type Intent } from './simulation'
import { useFightStore } from './store'

type ControlKey = keyof Intent
type Stick = { x:number; y:number; active:boolean }
const DEADZONE=.24
const MOVEMENT_KEYS:ControlKey[]=['left','right','depthUp','depthDown']

function emitControl(key:ControlKey,down:boolean){
  window.dispatchEvent(new CustomEvent('inyron:mobile-control',{detail:{key,down}}))
}
function haptic(ms=9){
  try{navigator.vibrate?.(ms)}catch{/* unavailable */}
}
function clearMobile(){
  window.dispatchEvent(new Event('inyron:mobile-reset'))
}
function Action({keyName,label,sub,ready=true,strong=false,className=''}:{keyName:ControlKey;label:string;sub?:string;ready?:boolean;strong?:boolean;className?:string}){
 const press=(e:ReactPointerEvent<HTMLButtonElement>)=>{e.preventDefault();try{e.currentTarget.setPointerCapture(e.pointerId)}catch{/* synthetic/unsupported pointer capture */}e.currentTarget.dataset.pressed='true';if(ready){emitControl(keyName,true);haptic(strong?18:8)}}
 const release=(e:ReactPointerEvent<HTMLButtonElement>)=>{e.preventDefault();delete e.currentTarget.dataset.pressed;emitControl(keyName,false)}
 return <button type="button" className={'mfc-button '+className} data-ready={ready?'true':'false'} aria-label={sub?label+' '+sub:label}
  onPointerDown={press} onPointerUp={release} onPointerCancel={release} onContextMenu={e=>e.preventDefault()}>
  <b>{label}</b>{sub&&<small>{sub}</small>}
 </button>
}

export function MobileFightControls(){
 const player=useFightStore(s=>s.player),status=useFightStore(s=>s.status),paused=useFightStore(s=>s.paused)
 const combo=player.comboCount,impact=useFightStore(s=>s.impactCue)
 const [stick,setStick]=useState<Stick>({x:0,y:0,active:false})
 const activeDirections=useRef<Record<string,boolean>>({})
 const joystickRef=useRef<HTMLDivElement>(null)

 useEffect(()=>()=>clearMobile(),[])
 const setDirection=(key:ControlKey,next:boolean)=>{
   if(!!activeDirections.current[key]===next)return
   activeDirections.current[key]=next;emitControl(key,next)
 }
 const updateStick=(clientX:number,clientY:number)=>{
   const node=joystickRef.current;if(!node)return
   const rect=node.getBoundingClientRect(),cx=rect.left+rect.width/2,cy=rect.top+rect.height/2
   const radius=Math.min(rect.width,rect.height)*.34,dx=clientX-cx,dy=clientY-cy
   const mag=Math.hypot(dx,dy),scale=mag>radius?radius/mag:1
   const x=(dx*scale)/radius,y=(dy*scale)/radius
   setStick({x,y,active:true})
   setDirection('left',x<-DEADZONE);setDirection('right',x>DEADZONE)
   setDirection('depthUp',y<-DEADZONE);setDirection('depthDown',y>DEADZONE)
 }
 const releaseStick=()=>{
   for(const key of MOVEMENT_KEYS)setDirection(key,false)
   setStick({x:0,y:0,active:false})
 }
 const pointer=useRef<number|null>(null)
 const beginStick=(e:ReactPointerEvent<HTMLDivElement>)=>{
   e.preventDefault();pointer.current=e.pointerId;try{e.currentTarget.setPointerCapture(e.pointerId)}catch{/* synthetic/unsupported pointer capture */}haptic(6);updateStick(e.clientX,e.clientY)
 }
 const moveStick=(e:ReactPointerEvent<HTMLDivElement>)=>{if(pointer.current===e.pointerId)updateStick(e.clientX,e.clientY)}
 const endStick=(e:ReactPointerEvent<HTMLDivElement>)=>{if(pointer.current!==e.pointerId)return;e.preventDefault();pointer.current=null;releaseStick()}

 const ground=player.y<=.001
 const ready={
   light:player.stamina>=STRIKES.light.stamina,
   heavy:player.stamina>=STRIKES.heavy.stamina,
   special:player.superMeter>=100&&player.stamina>=STRIKES.special.stamina,
   throw:player.stamina>=STRIKES.throw.stamina,
   quantum:player.quantumCooldown<=0&&player.stamina>=(player.quantumCost??22),
   superJump:ground,flight:player.y>=.45&&player.flightFuel>.08,
   boost:player.aerialMode==='flight'&&player.flightFuel>.04,
   dive:player.y>.05&&!ground,
 }
 if(paused||status==='match-over'||status==='round-over')return null
 return <section className="mobile-fight-controls" aria-label="Fight mobile controls" onContextMenu={e=>e.preventDefault()}>
   <div className="mfc-left">
    <div className="mfc-stick" ref={joystickRef} onPointerDown={beginStick} onPointerMove={moveStick} onPointerUp={endStick} onPointerCancel={endStick}>
      <span className="mfc-stick-ring"/><span className="mfc-stick-axis"/>
      <i className="mfc-stick-knob" style={{transform:`translate(calc(-50% + ${stick.x*34}px),calc(-50% + ${stick.y*34}px))`}}/>
      <em>MOVE / DEPTH</em>
    </div>
    <div className="mfc-mobility">
      <Action keyName="jump" label="JUMP" sub="AIR"/>
      <Action keyName="dash" label="DASH" sub="EVADE" className="mfc-hold"/>
      <Action keyName="quantum" label="Q" sub="SHIFT" ready={ready.quantum}/>
    </div>
   </div>
   <div className="mfc-air">
     <Action keyName="superJump" label="↑↑" sub="SUPER" ready={ready.superJump}/>
     <Action keyName="flight" label="FLY" sub="TOGGLE" ready={ready.flight}/>
     <Action keyName="boost" label="BOOST" sub="FLIGHT" ready={ready.boost} className="mfc-hold"/>
     <Action keyName="dive" label="DIVE" sub="DROP" ready={ready.dive} className="mfc-hold"/>
   </div>

   <div className="mfc-right">
     <div className="mfc-combat-status">
       <span>{impact?.source==='player'&&impact.kind!=='block'&&impact.kind!=='parry'?impact.kind.toUpperCase():'RIFT COMBAT'}</span>
       {combo>1&&<strong>{combo} HIT COMBO</strong>}
     </div>
     <Action keyName="block" label="G" sub="GUARD / PARRY" className="mfc-guard mfc-hold"/>
     <div className="mfc-attacks">
       <Action keyName="light" label="L" sub="LIGHT" ready={ready.light}/>
       <Action keyName="heavy" label="H" sub="HEAVY" ready={ready.heavy} strong/>
       <Action keyName="special" label="SP" sub="SPECIAL" ready={ready.special} strong className="mfc-special"/>
       <Action keyName="throw" label="TH" sub="THROW" ready={ready.throw}/>
     </div>
   </div>
 </section>
}
